import {createContext, Dispatch, SetStateAction, useEffect, useRef, useState} from 'react'
import qs from 'qs'
import {ID, QueryResponseContextProps, QueryState} from './models'

function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState)
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== ''
}

// Example: page=1&items_per_page=10&sort=id&order=desc&search=a&filter_name=a&filter_online=false
function stringifyRequestQuery(state: QueryState): string {
  const pagination = qs.stringify(state, {filter: ['page', 'items_per_page'], skipNulls: true})
  const sort = qs.stringify(state, {filter: ['sort', 'order'], skipNulls: true})
  const search = isNotEmpty(state.search)
    ? qs.stringify(state, {filter: ['search'], skipNulls: true})
    : ''

  const filter = state.filter
    ? Object.entries(state.filter as Object)
        .filter((obj) => isNotEmpty(obj[1]))
        .map((obj) => {
          return `filter_${obj[0]}=${obj[1]}`
        })
        .join('&')
    : ''

  return [pagination, sort, search, filter]
    .filter((f) => f)
    .join('&')
    .toLowerCase()
}

function parseRequestQuery(query: string): QueryState {
  const cache: unknown = qs.parse(query)
  return cache as QueryState
}

function calculatedGroupingIsDisabled<T>(isLoading: boolean, data: Array<T> | undefined): boolean {
  if (isLoading) {
    return true
  }

  return !data || !data.length
}

function calculateIsAllDataSelected<T>(data: Array<T> | undefined, selected: Array<ID>): boolean {
  if (!data) {
    return false
  }

  return data.length > 0 && data.length === selected.length
}

function groupingOnSelect(
  id: ID,
  selected: Array<ID>,
  setSelected: Dispatch<SetStateAction<Array<ID>>>
) {
  if (!id) {
    return
  }

  if (selected.includes(id)) {
    setSelected(selected.filter((itemId) => itemId !== id))
  } else {
    const updatedSelected = [...selected]
    updatedSelected.push(id)
    setSelected(updatedSelected)
  }
}

function groupingOnSelectAll<T>(
  isAllSelected: boolean,
  setSelected: Dispatch<SetStateAction<Array<ID>>>,
  data?: Array<T & {id?: ID}>
) {
  if (isAllSelected) {
    setSelected([])
    return
  }

  if (!data || !data.length) {
    return
  }

  setSelected(data.filter((item) => item.id).map((item) => item.id))
}

// Hook
function useDebounce(value: string | undefined, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay] // Only re-call effect if value or delay changes
  )
  return debouncedValue
}

interface LoadingMessageStep {
  delay: number
  text: string
}

function useProgressiveLoadingMessage(
  isLoading: boolean,
  steps: LoadingMessageStep[] = [
    { delay: 20000, text: 'Processing request... This may take a moment' },
    {
      delay: 40000,
      text: "Gathering the final results. We're on it! (Estimated completion: 30-60 sec)",
    },
    { delay: 60000, text: 'Almost there! Just finalizing the large files now.' },
  ]
) {
  const [message, setMessage] = useState('')
  const timersRef = useRef<number[]>([])

  useEffect(() => {
    const clearTimers = () => {
      timersRef.current.forEach((id) => clearTimeout(id))
      timersRef.current = []
    }

    if (isLoading) {
      clearTimers()
      setMessage('')

      timersRef.current = steps.map((item) =>
        window.setTimeout(() => setMessage(item.text), item.delay)
      )
    } else {
      clearTimers()
      setMessage('')
    }

    return () => clearTimers()
  }, [isLoading, steps])

  return message
}

const extractFilterValue = (val: any) => {
  if (!val) return undefined;
  if (typeof val === "string") return val;
  if (typeof val === "object") return val.label ?? val.value;
  return undefined;
};

const formatActionTypeLabel = (value: string) => {
  return value
    .toLowerCase()
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export {
  createResponseContext,
  stringifyRequestQuery,
  parseRequestQuery,
  calculatedGroupingIsDisabled,
  calculateIsAllDataSelected,
  groupingOnSelect,
  groupingOnSelectAll,
  useDebounce,
  isNotEmpty,
  useProgressiveLoadingMessage,
  extractFilterValue,
  formatActionTypeLabel
}

export const formatDateForPayload = (date: Date) => {
  const pad = (n: number) => n.toString().padStart(2, "0");

  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    " " +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes()) +
    ":" +
    pad(date.getSeconds())
  );
};

export const readApiResponseBody = (source: any) => {
  const root = source?.payload ?? source

  if (
    root != null &&
    typeof root === 'object' &&
    !Array.isArray(root) &&
    (root.status_code != null || root.message != null || root.massage != null)
  ) {
    return root
  }

  const data =
    root?.data ??
    source?.data ??
    source?.response?.data ??
    source?.error?.data ??
    root ??
    null

  if (
    data != null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    data.data != null &&
    typeof data.data === 'object' &&
    !Array.isArray(data.data) &&
    (data.data.status_code != null || data.data.message != null || data.data.massage != null)
  ) {
    return data.data
  }

  return data
}

export const getApiErrorMessage = (err: any, fallback = 'Something went wrong') => {
  // RTK unwrap() often throws:
  // - the "rejectWithValue" payload directly, OR
  // - { payload: ... }, OR
  // - axios-like { response: { data: ... } }
  const root = err?.payload ?? err
  const body = readApiResponseBody(err)

  const bodyMessage = body?.message || body?.massage || body?.error
  const detail = body?.errors?.detail || root?.errors?.detail

  if (detail && bodyMessage && typeof detail === 'string') {
    return `${bodyMessage} - ${detail}`
  }
  
  if (detail && typeof detail === 'string') {
    return detail
  }

  return (
    bodyMessage ||
    (Array.isArray(body?.errors) && body.errors[0]?.message) ||
    root?.message ||
    root?.massage ||
    err?.message ||
    fallback
  )
}

export const getApiPayloadStatusCode = (source: any): number | string | undefined => {
  const body = readApiResponseBody(source)
  const root = source?.payload ?? source
  return body?.status_code ?? root?.status_code
}

export const isApiPayloadSuccess = (source: any): boolean => {
  const statusCode = getApiPayloadStatusCode(source)
  if (statusCode != null) {
    const code = Number(statusCode)
    return !Number.isNaN(code) && code >= 200 && code < 300
  }

  const root = source?.payload ?? source
  if (root?.status >= 400) return false

  return source?.meta?.requestStatus !== 'rejected'
}

export const getApiSuccessMessage = (source: any, fallback?: string): string | undefined => {
  const body = readApiResponseBody(source)
  return body?.message || body?.massage || body?.success || fallback
}

export type ThunkToastHandlers = {
  successToast?: (message: string) => void
  errorToast?: (message: string) => void
}

export type HandleThunkResponseOptions = {
  successMessage?: string
  errorFallback?: string
  onSuccess?: (response: unknown) => void
  onError?: (message: string, response: unknown) => void
  showSuccessToast?: boolean
  showErrorToast?: boolean
}

/** Normalize RTK thunk results: show API error/success text and run callbacks. */
export function handleThunkResponse(
  response: unknown,
  handlers: ThunkToastHandlers & HandleThunkResponseOptions
): boolean {
  const {
    successToast,
    errorToast,
    successMessage,
    errorFallback = 'Something went wrong',
    onSuccess,
    onError,
    showSuccessToast = true,
    showErrorToast = true,
  } = handlers

  if (!isApiPayloadSuccess(response)) {
    const message = getApiErrorMessage(response, errorFallback)
    onError?.(message, response)
    if (showErrorToast && errorToast) {
      errorToast(message)
    }
    return false
  }

  onSuccess?.(response)
  if (showSuccessToast && successToast) {
    successToast(getApiSuccessMessage(response, successMessage) ?? successMessage ?? 'Success')
  }
  return true
}

export const blockInvalidChar = (e) => {
  if (['e', 'E', '+', '-', '.', '_'].includes(e.key)) {
    e.preventDefault()
  }
}

export const blockInvalidCharWithDecimal = (e) => {
  if (['e', 'E', '+', '-', '_'].includes(e.key)) {
    e.preventDefault()
  }
}

export const ensureNonNegative = (val: any, fallback: any = "") => {
  if (val === "" || val === null || val === undefined) return fallback;
  const num = Number(val);
  return isNaN(num) ? fallback : Math.max(0, num);
}

export const rsStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 44,
    borderRadius: 10,
    borderColor: state.isFocused ? "rgba(37,99,235,.45)" : "var(--line)",
    backgroundColor: state.isFocused ? "#ffffff" : "#fbfcff",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(37,99,235,.12)" : "none",
    transition: "box-shadow .12s ease, border-color .12s ease, background .12s ease",
    ":hover": { 
      borderColor: "rgba(37,99,235,.25)",
      backgroundColor: "#ffffff",
    },
  }),

  valueContainer: (base: any) => ({
    ...base,
    padding: "2px 12px",
  }),

  input: (base: any) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),

  placeholder: (base: any) => ({
    ...base,
    color: "var(--muted)",
    fontWeight: 600,
  }),

  singleValue: (base: any) => ({
    ...base,
    color: "var(--text)",
    fontWeight: 650,
  }),

  indicatorSeparator: () => ({ display: "none" }),

  menuPortal: (base: any) => ({
    ...base,
    zIndex: 30000,
  }),

  menu: (base: any) => ({
    ...base,
    borderRadius: 10,
    overflow: "hidden",
    border: "1px solid var(--line)",
    boxShadow: "var(--shadow)",
  }),

  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "rgba(37,99,235,.14)"
      : state.isFocused
        ? "rgba(37,99,235,.08)"
        : "#ffffff",
    color: "var(--text)",
  }),

  multiValue: (base: any) => ({
    ...base,
    borderRadius: 10,
    backgroundColor: "rgba(37,99,235,.10)",
    border: "1px solid rgba(37,99,235,.18)",
  }),

  multiValueLabel: (base: any) => ({
    ...base,
    fontWeight: 800,
    color: "#163a86",
  }),
};

export const splitParts = (s?: string) =>
  String(s || "")
    .split("-")
    .map((x) => x.trim())
    .filter(Boolean);

export const buildOptionsFromValue = (value?: string) => {
  const parts = String(value || "")
    .split("-")
    .map((s) => s.trim())
    .filter(Boolean);

  const out: Record<string, string> = {};
  parts.forEach((p, i) => (out[`part_${i + 1}`] = p));

  if (Object.keys(out).length === 0) out.part_1 = "";
  return out;
};

// helpers (put near top of MainColumn.tsx, inside component or outside)
export const formatDateTime = (iso?: string) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  // Example: Feb 18, 2026, 01:19 PM
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const timeAgo = (iso?: string) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";

  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / (1000 * 60));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;

  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
};

export const buildEditCustomFieldsFormik = (rows: any[] = [], metaList: any[] = []) => {
  const metaById = new Map<number, any>();
  metaList.forEach((m: any) => metaById.set(Number(m.id), m));
  const out: Record<string, any> = {};
  for (const r of rows) {
    const fieldId = Number(r.platform_field_id ?? r.id);
    const meta = metaById.get(fieldId);
    const type = meta?.input_type;
    // these key names depend on your response:
    const selectedId =
      r.platform_field_data_id ??
      r.platform_field_data ??
      r.field_data_id ??
      r.selected_id;

    const value =
      r.value ??
      r.api_name ??
      r.name ??
      r.text ??
      "";

    if (type === "MULTISELECT") {
      const curr = Array.isArray(out[fieldId]) ? out[fieldId] : [];
      if (selectedId) curr.push(String(selectedId));
      out[fieldId] = curr;
      continue;
    }
    if (type === "SELECT") {
      out[fieldId] = selectedId ? String(selectedId) : "";
      continue;
    }
    if (type === "CHECKBOX") {
      out[fieldId] = parseCheckboxValue(value);
      continue;
    }
    out[fieldId] = value === null || value === undefined ? "" : String(value);
  }
  return out;
};

export const detectHasVariant = (product: any) => {
  const variants = product?.variants;
  if (!Array.isArray(variants) || variants.length === 0) return false;

  // 1) Check product-level options dict – if ANY option has values, it's a variant product
  const opts = product?.options;
  if (opts && typeof opts === "object") {
    const hasOptionValues = Object.values(opts).some((optObj: any) => {
      const vals = Array.isArray(optObj?.values) ? optObj.values : [];
      return vals.length > 0;
    });
    if (hasOptionValues) return true;
  }

  // 2) Fallback: check ALL variants (not just first) for option value bindings
  const hasBindings = variants.some((v: any) => {
    const v1 = v?.platform_product_option_value1;
    const v2 = v?.platform_product_option_value2;
    const v3 = v?.platform_product_option_value3;
    return (
      (v1 !== null && v1 !== undefined) ||
      (v2 !== null && v2 !== undefined) ||
      (v3 !== null && v3 !== undefined)
    );
  });

  return hasBindings;
};

export const isEmptyValue = (v: any) =>
  v === "" || v === null || v === undefined || (Array.isArray(v) && v.length === 0);

export const buildCustomFieldsPayloadForCreate = (
  customFieldsObj: Record<string, any>,
  customFieldsList: any[]
) => {
  const metaById = new Map<number, any>();
  (customFieldsList || []).forEach((f: any) => metaById.set(Number(f.id), f));

  const result: any[] = [];

  Object.entries(customFieldsObj || {}).forEach(([fieldIdStr, raw]) => {
    const fieldId = Number(fieldIdStr);
    if (!fieldId) return;
    const type = metaById.get(fieldId)?.input_type;
    // Skip empty values
    if (isEmptyValue(raw)) {
      return;
    }

    // MULTISELECT -> one entry per selected value with platform_field_data_id (singular)
    if (type === "MULTISELECT") {
      const ids = raw.map((x: any) => Number(x)).filter((n: number) => !Number.isNaN(n));

      ids.forEach((dataId: number) => {
        result.push({
          platform_field_id: fieldId,
          platform_field_data_id: dataId,
          value: "",
        });
      });
      return;
    }

    // SELECT -> single selected id
    if (type === "SELECT") {
      result.push({ platform_field_id: fieldId, platform_field_data_id: Number(raw), value: "", });
      return;
    }

    // CHECKBOX
    if (type === "CHECKBOX") {
      result.push({platform_field_id: fieldId, platform_field_data_id: null, value: parseCheckboxValue(raw), });
      return;
    }

    // other types
    result.push({platform_field_id: fieldId, platform_field_data: null, value: String(raw), });
  });

  return result;
};

export const buildCustomFieldsPayloadForEdit = (
  customFieldsObj: Record<string, any>,
  customFieldsList: any[],
  productCustomFieldsRaw: any[]
) => {
  const metaById = new Map<number, any>();
  (customFieldsList || []).forEach((f: any) => metaById.set(Number(f.id), f));

  const existingByField = new Map<number, any[]>();
  (productCustomFieldsRaw || []).forEach((r: any) => {
    const fid = Number(
      r.platform_field ??
        r.platform_field_id ??
        r.field_id ??
        r.platform_field?.id
    );

    if (!fid) return;

    if (!existingByField.has(fid)) existingByField.set(fid, []);
    existingByField.get(fid)!.push(r);
  });

  const isEmptyValue = (v: any) =>
    v === "" ||
    v === null ||
    v === undefined ||
    (Array.isArray(v) && v.length === 0);

  const result: any[] = [];

  Object.entries(customFieldsObj || {}).forEach(([fieldIdStr, raw]) => {
    const fieldId = Number(fieldIdStr);
    if (!fieldId) return;

    const type = metaById.get(fieldId)?.input_type;
    const existingRows = existingByField.get(fieldId) || [];
    const row0 = existingRows[0];
    const empty = isEmptyValue(raw);

    if (empty && !row0?.id) return;

    // MULTISELECT -> one entry per option: keep, unselect (null), or add new
    if (type === "MULTISELECT") {
      const newSelectedIds = Array.isArray(raw)
        ? raw.map((x: any) => Number(x)).filter((n: number) => !Number.isNaN(n))
        : [];

      // If never selected before and still empty, send nothing
      if (newSelectedIds.length === 0 && existingRows.length === 0) {
        return;
      }

      // Process existing DB rows: keep or unselect (set null)
      const matchedExistingOptionIds = new Set<number>();
      existingRows.forEach((existingRow: any) => {
        const savedOptionId = Number(
          existingRow.platform_field_data_id ??
          existingRow.platform_field_data ??
          existingRow.value_id
        );
        const isStillSelected = newSelectedIds.includes(savedOptionId);

        if (isStillSelected) matchedExistingOptionIds.add(savedOptionId);

        result.push({
          ...(existingRow?.id ? { id: Number(existingRow.id) } : {}),
          platform_field_id: fieldId,
          platform_field_data_id: isStillSelected ? savedOptionId : null,
          value: "",
        });
      });

      // Process brand new selections (no matching DB row)
      newSelectedIds
        .filter((optId) => !matchedExistingOptionIds.has(optId))
        .forEach((newOptionId: number) => {
          result.push({
            platform_field_id: fieldId,
            platform_field_data_id: newOptionId,
            value: "",
          });
        });

      return;
    }

    if (type === "SELECT") {
      result.push({
        ...(row0?.id ? { id: Number(row0.id) } : {}),
        platform_field_id: fieldId,
        platform_field_data_id: empty ? null : Number(raw),
        value: "",
      });
      return;
    } 

    if (type === "CHECKBOX") {
      const boolValue = parseCheckboxValue(raw);

      // skip if false and no existing id
      if (!boolValue && !row0?.id) return;

      result.push({
        ...(row0?.id ? { id: Number(row0.id) } : {}),
        platform_field_id: fieldId,
        platform_field_data: null,
        value: boolValue,
      });

      return;
    } 

    result.push({
      ...(row0?.id ? { id: Number(row0.id) } : {}),
      platform_field_id: fieldId,
      platform_field_data: null,
      value: empty ? "" : String(raw),
    }); 
  });

  return result;
}; 

export const buildOptionsValuesPayload = (
  options: any[] = [],
  mode: "create" | "edit" = "create"
) => {
  return (options || [])
    .filter((opt) => String(opt?.name ?? "").trim())
    .map((opt) => {
      const name = String(opt.name).trim();

      // Your UI sometimes has `values` and sometimes `__values`
      const rawValues =
        Array.isArray(opt.values) && opt.values.length
          ? opt.values
          : Array.isArray(opt.__values)
            ? opt.__values
            : [];

      const cleanedValues = rawValues
        .map((v: any) => String(v).trim())
        .filter(Boolean);

      // value -> id mapping (only in edit mode, if you stored it)
      const valueIds: Record<string, number> =
        opt?.value_ids && typeof opt.value_ids === "object" ? opt.value_ids : {};

      return {
        ...(mode === "edit" && opt?.id ? { id: Number(opt.id) } : {}), // option id
        ...(opt?.position !== undefined ? { position: Number(opt.position) } : {}),
        name,
        values: cleanedValues.map((val: string) => {
          const existingId = valueIds[val];
          // if edit + id exists -> send {id,value}, else send {value}
          return mode === "edit" && existingId
            ? { id: Number(existingId), value: val }
            : { value: val };
        }), 
      }; 
    }) 
    .filter((opt) => opt.values.length > 0); 
}; 

export const buildCustomFieldsInitialValues = (productCustomFields: any[] = [], customFieldsList: any[] = []) => {
  const metaById = new Map<number, any>();
  (customFieldsList || []).forEach((f: any) => metaById.set(Number(f.id), f));

  const out: Record<string, any> = {};
  for (const row of productCustomFields) {
    const fieldId = Number(row?.platform_field_id ?? row?.field_id);
    if (!fieldId) continue;
    const type = metaById.get(fieldId)?.input_type;
    const dataId = row?.platform_field_data_id ?? row?.platform_field_data; // for select/multi
    const value = row?.value ?? row?.api_name ?? ""; // for text-like

    if (type === "MULTISELECT") {
      const curr = Array.isArray(out[fieldId]) ? out[fieldId] : [];
      if (dataId) curr.push(Number(dataId));
      out[fieldId] = curr;
      continue;
    } 
    if (type === "SELECT") {
      out[fieldId] = dataId ? Number(dataId) : "";
      continue;
    } 
    if (type === "CHECKBOX") {
      out[fieldId] = parseCheckboxValue(value);
      continue;
    }
    out[fieldId] = value ?? "";
  } 
  return out;
}; 

export const buildAdjustInventoryPayload = (
  values: any,
  productId: number,
  variantsSource: any[]
) => {
  const apiVariants = Array.isArray(variantsSource) ? variantsSource : [];

  if (!productId || !apiVariants.length) {
    return {
      convert: false,
      items: [],
    }; 
  } 

  // non-variant product
  if (!values?.isProductHasVariant) {
    const variant = apiVariants[0];
    if (!variant?.id) {
      return {
        convert: false,
        items: [],
      }; 
    } 

    const sku =
      values?.varients_data?.[0]?.sku ||
      variant?.sku ||
      "";

    const items = (values?.inventory || [])
      .filter((row: any) => Number(row?.id) && Number(row?.quantity) > 0)
      .map((row: any) => ({
        variant_id: Number(variant.id),
        product_id: Number(productId),
        sku,
        quantity: Number(row.quantity) || 0,
        warehouse_id: Number(row.id),
      }));

    return {
      convert: false,
      items,
    };
  }

  // variant product
  const items = (values?.varients_data || []).flatMap((variantRow: any, index: number) => {
    const apiVariant =
      apiVariants.find((v: any) => Number(v?.id) === Number(variantRow?.variant_id)) ||
      apiVariants[index];

    if (!apiVariant?.id) return [];

    const sku = variantRow?.sku || apiVariant?.sku || "";

    const inventoryRows = Array.isArray(variantRow?.inventory)
      ? variantRow.inventory
      : [];

    return inventoryRows
      .filter((row: any) => Number(row?.id) && Number(row?.quantity) > 0)
      .map((row: any) => ({
        variant_id: Number(apiVariant.id),
        product_id: Number(productId),
        sku,
        quantity: Number(row.quantity) || 0,
        warehouse_id: Number(row.id),
      }));
  });

  return {
    convert: false,
    items,
  };
};

export const mapInventoryToFormikValues = (invRows: any[] = []) => {
  const firstVariant = invRows?.[0];
  const locations = Array.isArray(firstVariant?.locations) ? firstVariant.locations : [];

  return locations.map((loc: any) => ({
    id: Number(loc.location_id),
    location_id: Number(loc.location_id),
    location: String(loc.location_name ?? ''),
    location_name: String(loc.location_name ?? ''),
    quantity: Number(loc.available_quantity ?? 0),
    available_quantity: Number(loc.available_quantity ?? 0),
    reserve_quantity: Number(loc.reserve_quantity ?? 0),
    on_hand: Number(loc.on_hand ?? 0),
  }));
};

type CustomFieldMode = "create" | "edit";

export const isBooleanLike = (value: any) => {
  if (typeof value === "boolean") return true;
  if (typeof value === "number") return value === 0 || value === 1;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return ["true", "false", "1", "0", "yes", "no"].includes(normalized);
  }

  return false;
};

export const parseCheckboxValue = (raw: any): boolean => {
  if (typeof raw === "boolean") return raw;

  if (typeof raw === "number") return raw === 1;

  if (typeof raw === "string") {
    const normalized = raw.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }

  if (raw && typeof raw === "object") {
    const nested =
      raw?.value ??
      raw?.name ??
      raw?.label ??
      raw?.api_name ??
      raw?.field_value;

    return parseCheckboxValue(nested);
  }

  return false;
};

export const getCheckboxRawOrDefault = (
  fieldId: number,
  customFieldsObj: Record<string, any>,
  customFieldsList: any[]
) => {
  const hasOwn = Object.prototype.hasOwnProperty.call(customFieldsObj || {}, String(fieldId));

  if (hasOwn && isBooleanLike(customFieldsObj[String(fieldId)])) {
    return customFieldsObj[String(fieldId)];
  }

  const fieldMeta = (customFieldsList || []).find((f: any) => Number(f?.id) === Number(fieldId));
  return fieldMeta?.default_value;
};

export const buildVariantCustomFieldsPayloadForCreate = (
  customFieldsObj: Record<string, any>,
  customFieldsList: any[]
) => {
  const metaById = new Map<number, any>();
  (customFieldsList || []).forEach((f: any) => metaById.set(Number(f.id), f));

  const result: any[] = [];

  (customFieldsList || []).forEach((field: any) => {
    const fieldId = Number(field?.id);
    if (!fieldId) return;

    const type = field?.input_type;
    let raw = customFieldsObj?.[String(fieldId)];

    if (type === "CHECKBOX") {
      raw = getCheckboxRawOrDefault(fieldId, customFieldsObj, customFieldsList);

      result.push({
        platform_field_id: fieldId,
        platform_field_data_id: null,
        value: parseCheckboxValue(raw),
      });

      return;
    }

    if (raw === undefined) return;
    if (isEmptyValue(raw)) return;

    if (type === "MULTISELECT") {
      const ids = Array.isArray(raw)
        ? raw.map((x: any) => Number(x)).filter((n: number) => !Number.isNaN(n))
        : [];

      ids.forEach((id: number) => {
        result.push({
          platform_field_id: fieldId,
          platform_field_data_id: id,
          value: "",
        });
      });

      return;
    }

    if (type === "SELECT") {
      result.push({
        platform_field_id: fieldId,
        platform_field_data_id: Number(raw),
        value: "",
      });
      return;
    }

    result.push({
      platform_field_id: fieldId,
      platform_field_data_id: null,
      value: String(raw),
    });
  });

  return result;
};

export const buildVariantCustomFieldsPayloadForEdit = (
  customFieldsObj: Record<string, any>,
  customFieldsList: any[],
  existingVariantCustomFields: any[]
) => {
  const metaById = new Map<number, any>();
  (customFieldsList || []).forEach((f: any) => metaById.set(Number(f.id), f));

  const existingByField = new Map<number, any[]>();

  (existingVariantCustomFields || []).forEach((row: any) => {
    const fieldId = Number(
      row?.platform_field ??
      row?.platform_field_id ??
      row?.custom_field_id ??
      row?.field_id ??
      row?.platform_field?.id
    );

    if (!fieldId) return;

    if (!existingByField.has(fieldId)) {
      existingByField.set(fieldId, []);
    }

    existingByField.get(fieldId)!.push(row);
  });

  const result: any[] = [];

  (customFieldsList || []).forEach((field: any) => {
    const fieldId = Number(field?.id);
    if (!fieldId) return;

    const type = field?.input_type;
    const existingRows = existingByField.get(fieldId) || [];
    const row0 = existingRows[0];
    let raw = customFieldsObj?.[String(fieldId)];

    if (type === "CHECKBOX") {
      raw = getCheckboxRawOrDefault(fieldId, customFieldsObj, customFieldsList);

      result.push({
        ...(row0?.id ? { id: Number(row0.id) } : {}),
        platform_field_id: fieldId,
        platform_field_data_id: null,
        value: parseCheckboxValue(raw),
      });

      return;
    }

    if (raw === undefined) return;

    const empty = isEmptyValue(raw);

    if (empty && !row0?.id) return;

    // MULTISELECT -> one entry per option: keep, unselect (null), or add new
    if (type === "MULTISELECT") {
      const newSelectedIds = Array.isArray(raw)
        ? raw.map((x: any) => Number(x)).filter((n: number) => !Number.isNaN(n))
        : [];

      // If never selected before and still empty, send nothing
      if (newSelectedIds.length === 0 && existingRows.length === 0) {
        return;
      }

      // Process existing DB rows: keep or unselect (set null)
      const matchedExistingOptionIds = new Set<number>();
      existingRows.forEach((existingRow: any) => {
        const savedOptionId = Number(
          existingRow.platform_field_data_id ??
          existingRow.platform_field_data ??
          existingRow.value_id
        );
        const isStillSelected = newSelectedIds.includes(savedOptionId);

        if (isStillSelected) matchedExistingOptionIds.add(savedOptionId);

        result.push({
          ...(existingRow?.id ? { id: Number(existingRow.id) } : {}),
          platform_field_id: fieldId,
          platform_field_data_id: isStillSelected ? savedOptionId : null,
          value: "",
        });
      });

      // Process brand new selections (no matching DB row)
      newSelectedIds
        .filter((optId) => !matchedExistingOptionIds.has(optId))
        .forEach((newOptionId: number) => {
          result.push({
            platform_field_id: fieldId,
            platform_field_data_id: newOptionId,
            value: "",
          });
        });

      return;
    }

    if (type === "SELECT") {
      result.push({
        ...(row0?.id ? { id: Number(row0.id) } : {}),
        platform_field_id: fieldId,
        platform_field_data_id: empty ? null : Number(raw),
        value: "",
      });
      return;
    }

    result.push({
      ...(row0?.id ? { id: Number(row0.id) } : {}),
      platform_field_id: fieldId,
      platform_field_data_id: null,
      value: empty ? "" : String(raw),
    });
  });

  return result;
};

export const buildVariantCustomFieldsPayload = ({
  mode,
  customFieldsObj,
  customFieldsList,
  existingCustomFields = [],
}: {
  mode: "create" | "edit";
  customFieldsObj: Record<string, any>;
  customFieldsList: any[];
  existingCustomFields?: any[];
}) => {
  if (mode === "edit") {
    return buildVariantCustomFieldsPayloadForEdit(
      customFieldsObj,
      customFieldsList,
      existingCustomFields
    );
  }

  return buildVariantCustomFieldsPayloadForCreate(
    customFieldsObj,
    customFieldsList
  );
};

export const getSortOrder = (
  direction?: 'asc' | 'desc'
): 'ASC' | 'DESC' | '' => {
  if (direction === 'asc') return 'ASC'
  if (direction === 'desc') return 'DESC'
  return ''
}
